import { useContext, useState } from "react";

import ProposalInputContext from "../../../context/ProposalInputContext";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Chip from '@mui/material/Chip';
import Typography from "@mui/material/Typography";

import ClearIcon from '@mui/icons-material/Clear';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import InputFileUpload from "../../layout/InputFileUpload";
import ExpandableSectionHeader from "./ExpandableSectionHeader";

const DocumentUploadForm = () => {
  const {
    values, formErrors, collapseValues, setCollapseValue, handleMinutesUpload,
    handleMinuteRemove, handleOtherFileUpload, handleOtherFileRemove, sectionStyle
  } = useContext(ProposalInputContext);

  const [isDragMinutes, setIsDragMinutes] = useState(false);
  const [isDragOther, setIsDragOther] = useState(false);

  const triggerContextUpload = (handler, files) => {
    if (files && files.length > 0) {
      handler(files);
    }
  };

  const handleMinutesDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragMinutes(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      triggerContextUpload(handleMinutesUpload, e.dataTransfer.files);
    }
  };

  const handleOtherDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOther(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      triggerContextUpload(handleOtherFileUpload, e.dataTransfer.files);
    }
  };

  const handleDragOver = (e, setDragState) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState(true);
  };

  const handleDragLeave = (e, setDragState) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState(false);
  };

  return (
    <Grid container spacing={2} sx={sectionStyle}>
      <ExpandableSectionHeader
        sectionTitle='6. Documents Upload'
        showSection='showDocumentInfo'
        icon={<FolderOpenOutlinedIcon sx={{ color: "text.secondary" }} />}
        collapseValues={collapseValues}
        setCollapseValue={setCollapseValue}
      />

      {collapseValues?.showDocumentInfo &&
        <>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', ml: 0.5 }}
            >
              Drag & drop files into the box, or click the button below to browse.
            </Typography>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Box
              onDragOver={(e) => handleDragOver(e, setIsDragMinutes)}
              onDragLeave={(e) => handleDragLeave(e, setIsDragMinutes)}
              onDrop={handleMinutesDrop}
              sx={{
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                backgroundColor: isDragMinutes ? '#F1F5F9' : 'transparent',
                outline: isDragMinutes ? '2px dashed #0F172A' : 'none',
                mb: 2,
                mr: 1,
              }}
            >
              <TextField
                fullWidth
                label="Call transcript / Meeting minutes *"
                variant="outlined"
                name="meetingMinutes"
                disabled
                placeholder={isDragMinutes ? "Drop file here..." : ""}
                InputProps={{
                  startAdornment: values?.meetingMinutes ? (
                    <Chip
                      label={values?.meetingMinutes.name}
                      onDelete={handleMinuteRemove}
                      deleteIcon={<ClearIcon />}
                      sx={{ mr: 1, pointerEvents: 'auto' }}
                    />
                  ) : null,
                }}
                helperText={formErrors['meetingMinutes']}
                error={'meetingMinutes' in formErrors}
              />
            </Box>

            <InputFileUpload
              btnTxt="Upload Minutes"
              handleFileUpload={handleMinutesUpload}
              icon={<DescriptionIcon />}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Box
              onDragOver={(e) => handleDragOver(e, setIsDragOther)}
              onDragLeave={(e) => handleDragLeave(e, setIsDragOther)}
              onDrop={handleOtherDrop}
              sx={{
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                backgroundColor: isDragOther ? '#F1F5F9' : 'transparent',
                outline: isDragOther ? '2px dashed #0F172A' : 'none',
                mb: 2,
                mr: 1,
              }}
            >
              <TextField
                fullWidth
                label="Other Files"
                variant="outlined"
                name='otherFiles'
                disabled
                placeholder={isDragOther ? "Drop files here..." : ""}
                InputProps={{
                  startAdornment: (values?.otherFiles && values.otherFiles.length > 0 ?
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 0.5, pointerEvents: 'auto' }}>
                      {values?.otherFiles?.map(file => (
                        <Chip
                          key={file.name}
                          label={file.name}
                          onDelete={() => handleOtherFileRemove(file.name)}
                          deleteIcon={<ClearIcon />}
                          sx={{ pointerEvents: 'auto' }}
                        />
                      ))}
                    </Box>
                    : null
                  ),
                }}
                helperText={formErrors['otherFiles']}
                error={'otherFiles' in formErrors}
              />
            </Box>

            <InputFileUpload
              btnTxt='Upload other files'
              color={'primary'}
              handleFileUpload={handleOtherFileUpload}
              icon={<AttachFileIcon />}
            />
          </Grid>
        </>
      }
    </Grid>
  );
};

export default DocumentUploadForm;